import {Component, Input, OnInit, OnChanges, EventEmitter, Output} from '@angular/core';
import { UntypedFormArray, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {TypesService} from '../../../services';
import {WizardWidgetComponent} from '../wizard-widget/wizard-widget.component';
import {
  borrowerGuarantorValidator,
  notSameCompanyRelationShipValidator
} from '../../../_global/validators/custom-validators.directive';
import _ from 'lodash';
import {FormBuilderService, NotificationService} from '../../../services';
import {IndividualTrustModel} from '../../../models/widgets/trust-details-model';
import {IndividualCompanyModel} from '../../../models/widgets';
import {
  IndividualNonApplicantModel,
  IndividualPersonModel,
  IndividualRelationshipModel,
  IndividualPhoneModel,
  PreviousAddressModel
} from '../../../models/widgets/personal-details-model';
import {debounceTime, pairwise, startWith} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';
import {DataHandlerService} from '../../../services/data-handler.service';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ValidationPartialComponent } from '../partials';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { NgIf, NgFor } from '@angular/common';
import {
  AbnSearchComponent
} from '../../../../../firstmac-material-lib/projects/fmc-elements/src/lib/abn-search/abn-search.component';

@Component({
    selector: 'app-borrower-type-enhanced',
    templateUrl: 'borrower-type-enhanced.component.html',
    standalone: true,
    imports: [NgIf, MatCardModule, MatCardModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatFormFieldModule,
      MatSelectModule, MatSelectModule, NgFor, MatOptionModule, MatOptionModule, ValidationPartialComponent, MatCheckboxModule,
      MatCheckboxModule, MatInputModule, MatInputModule, AbnSearchComponent]
})
export class BorrowerTypeEnhancedComponent extends WizardWidgetComponent implements OnInit, OnChanges {
  @Input() public formGroup: UntypedFormGroup;
  @Input() public applicationType: string;
  @Input() public isLateInit = false; // if form is loaded via partial, don't emit borrower type changes
  @Input() public personList: UntypedFormArray; // true if personal, false if business, null if display all
  @Input() public trustList: UntypedFormArray; // true if personal, false if business, null if display all
  @Input() public companyList: UntypedFormArray; // true if personal, false if business, null if display all
  @Input() public isSMSF = false;
  @Input() public isFurtherAdvance = false;
  @Input() public isSMSFPanel = false;
  @Output() public borrowingTypeChange = new EventEmitter();
  @Output() public checkNonApplicantValidation = new EventEmitter<any>();

  public borrowingTypeSlug = 'borrowing-types-v2';
  public trustPurposeSlug = 'trust-purposes-vx';
  public borrowerCountRequired = false;
  public borrowingTypes = [];
  public trustTypes = [];
  public selectedType; // selected borrower type
  public smsfEnabled = environment.smsfEnabled;
  abnAcnValidator = '[0-9 ]*';
  securityTrustBorrowerCount = 0;
  securityTrustBorrowerType = '';
  mainBorrowerType = '';

  constructor(private typesService: TypesService,
              private formBuilderService: FormBuilderService,
              private notificationService: NotificationService,
              public dataHandlerService: DataHandlerService) {
    super(dataHandlerService);
  }

  async ngOnInit() {
    super.ngOnInit();
    this.opts.trustIndex = this.opts.trustIndex || 0;

    this.dataHandlerService.sendMainBorrowerType(this.formGroup.get('borrowerType').value);

    this.dataHandlerService.getSmsfData()
      .subscribe((value: any) => {
        if (this.formGroup.get('personList')) {
          if (value) {
            this.formGroup.get('personList').clearValidators();
          } else if (this.applicationType !== 'new-personal') {
            this.formGroup.get('personList').clearValidators();
          } else {
            this.formGroup.get('personList').clearValidators();
            this.formGroup.get('personList').setValidators(
              Validators.compose([
                borrowerGuarantorValidator()
              ])
            );
          }

          this.formGroup.get('personList').updateValueAndValidity();
        }
      });

    this.dataHandlerService.getSecurityTrustBorrowerType()
      .subscribe((value: any) => {
        this.securityTrustBorrowerType = value;
      });

    this.dataHandlerService.getMainBorrowerType().subscribe((result: any) => {
      this.mainBorrowerType = result;
    });

    this.dataHandlerService.getSecurityTrustBorrowerCount().subscribe((result: any) => {
      this.securityTrustBorrowerCount = result;
    });

    this.formGroup
      .get('borrowerType')
      .valueChanges.pipe(debounceTime(500), startWith(this.formGroup.get('borrowerType').value), pairwise())
      .subscribe(([prev, val]) => {
        if (prev !== val) {
          this.handleBorrowerTypeChange(val);
          const borrowerCount = this.getBorrowerCount(val);
          this.handleBorrowerCountChange(borrowerCount);
          if (this.isSMSFPanel) {
            this.formGroup.get('borrowerCount').patchValue(1);
            this.dataHandlerService.sendSecurityTrustBorrowerType(val);
          }
        }
      });

    this.formGroup
      .get('borrowerCount')
      .valueChanges.pipe(debounceTime(500), startWith(0), pairwise())
      .subscribe(([prev, val]) => {
        if (prev !== val) {
          this.handleBorrowerCountChange(val);
          if (this.isSMSFPanel) {
            this.dataHandlerService.sendSecurityTrustBorrowerCount(val);
            this.handleSecurityTrust(val);
          }
        }
      });

    this.formGroup
      .get('trustType')
      .valueChanges.pipe(debounceTime(500))
      .subscribe((val) => {
        this.handleBorrowerCountChange(this.formGroup.get('borrowerCount')?.value || 0);
        if (val) {
          while (!this.trustList.at(this.opts.trustIndex)) {
            this.addNewTrust();
          }
          this.trustList.at(this.opts.trustIndex).get('trustType').patchValue(val);
        }
      });

    this.formGroup
      .get('trustNoABN')
      .valueChanges.pipe(debounceTime(500))
      .subscribe((val) => {
        if (val) {
          this.formGroup.get('trustABN').reset();
          this.formGroup.get('trustABNResult').reset();
          this.formGroup.get('trustABN').clearValidators();
          this.formGroup.get('trustABN').updateValueAndValidity();
        } else {
          if (this.formGroup.get('borrowerType').value === 'trust-with-company' || this.formGroup.get('borrowerType').value === 'trust-with-individual') {
            this.toggleTrustSearches(true);
          }
        }

        if (this.trustList && this.trustList.length > 0) {
          this.trustList
            .at(this.opts.trustIndex)
            .get('trustName')
            .patchValue(null, {emitEvent: false});
          this.trustList.at(this.opts.trustIndex).get('trustNoABN').patchValue(val);
        }
      });

    this.formGroup
      .get('companyACN')
      .valueChanges.pipe(debounceTime(500))
      .subscribe((val) => {
        if (val === null || val === '') {
          if (this.formGroup.get('acnRegistered')) {
            this.formGroup.get('acnRegistered').reset();
          }
        }
      });

    await this.populateTypes();
  }

  handleSecurityTrust(borrowerCount = null) {
    // remove person with company
    // if (person.count < borrowerCount)
    //    just add the missing person then
    // patch company from last entry until smsfborrowerCOunt
    const deletedPersons = [];

    const personOrigCount = this.personList.length;
    for (let index = personOrigCount - 1; index >= 0; index--) {
      deletedPersons.splice(0, 0, this.personList.at(index).value);
      this.personList.removeAt(index);
    }
    if (this.personList.length < personOrigCount) {
      const {type, to, applicantType} = this.getBorrowingTypeMapping(this.mainBorrowerType);
      let deletedPersonIdx = 0;
      for (let index = this.personList.length; index < personOrigCount; index++) {
        this.addNewIndividual(type, to, applicantType, deletedPersons[deletedPersonIdx++]);
      }
    }

    let lastEntry = this.personList.length;
    const {type, to, applicantType} = this.getBorrowingTypeMapping(this.securityTrustBorrowerType || null);
    _.times(borrowerCount || this.securityTrustBorrowerCount, () => {
      if (lastEntry > 0) {
        const companyRelationshipFC = this.personList.controls[lastEntry - 1].get('companyRelationships');
        if (companyRelationshipFC) {
          companyRelationshipFC.patchValue([{from: lastEntry - 2, to, type}]);
          lastEntry--;
        }
      }
    });
  }

  async ngOnChanges(changes) {
    if (changes.applicationType && changes.applicationType.currentValue) {
      this.handleApplicationTypeChange(
        changes.applicationType.previousValue,
        changes.applicationType.currentValue
      );

      if (this.isFurtherAdvance) {
        if (this.personList.length && !this.trustList.length && !this.companyList.length) {
          this.applicationType = 'new-personal';
        } else if (this.personList.length && !this.trustList.length && this.companyList.length) {
          this.applicationType = 'new-company';
        } else {
          this.applicationType = 'new-trust';
        }
      }

      await this.populateTypes();
    }

    if (changes.isSMSF) {
      this.formGroup.get('borrowerCount').updateValueAndValidity();
    }

    if (changes.trustList) {
      this.trustList = changes.trustList.currentValue;
    }
  }

  extractABNDetails(abnResult) {
    return {
      abn:
        _.get(abnResult, 'australian-business-number._text') ||
        _.get(abnResult, 'australian-business-register-report.ABN._text'),
      acn: _.get(abnResult, 'australian-business-register-report.ASICNumber._text'),
      acnRegistered: _.get(
        abnResult,
        'australian-business-register-report.ABNStatusFromDate._text'
      ),
      tradingName: _.get(abnResult, 'australian-business-register-report.TradingName._text'),
      name: _.startCase(_.toLower(_.get(abnResult, 'match-name._text'))),
    };
  }

  public populateTypes() {
    return Promise.all([
      this.typesService.getTypesList(this.borrowingTypeSlug).then((res) => {
        this.borrowingTypes = res.filter((type) => {
          return (
            type.ext.applicationTypes && type.ext.applicationTypes.includes(this.applicationType)
          );
        });
        if (this.borrowingTypes.length) {
          // Security Trust can only be ‘Trust with Company Trustee’
          if (this.isSMSFPanel) {
            this.borrowingTypes = this.borrowingTypes.filter((type) => {
              return type.id === 'trust-with-company';
            });
          }

          this.selectedType = this.borrowingTypes.find(x => x.id === this.formGroup.get('borrowerType').value);
          if (this.selectedType) {
            this.formGroup.get('borrowerType').patchValue(this.selectedType.id);
          }

          if (this.isFurtherAdvance && this.applicationType === 'new-personal') {
            if (this.personList.length > 1) {
              this.formGroup.get('borrowerCount').disable();
            }

            this.borrowingTypes.forEach((type: any) => {
              if (this.personList.length === 1 && type.id === 'individual-with-non-applicant') {
                type.ext.disabled = false;
              } else {
                type.ext.disabled = true;
              }
            });
          } else {
            this.borrowingTypes.forEach((type: any) => {
              type.ext.disabled = false;
            });
          }
        }
      }),
      this.typesService.getTypesList(this.trustPurposeSlug).then((res) => {
        this.trustTypes = res.filter((type) => {
          return type.ext.Active;
        });
        if (this.opts.trustType) {
          this.formGroup.get('trustType').patchValue(this.opts.trustType);

          if (this.isSMSF && this.opts.trustType === 'security-trust') {
            this.formGroup.get('trustType').disable();
          } else {
            this.formGroup.get('trustType').enable();
          }
        }
      }),
      this.typesService.getTypesList('submission-configurations').then((res) => {
        const configs = res;
        this.smsfEnabled = !!_(configs)
          .chain()
          .find((t) => t.id === 'smsf')
          .get('ext.active')
          .value();
      }),
    ]);
  }

  addNewTrust() {
    const trustModel = new IndividualTrustModel();
    const trustFg = this.formBuilderService.convertObjectToFormGroup(trustModel);
    this.formBuilderService.setValidationRules(new IndividualTrustModel(), trustFg);
    this.trustList.push(trustFg);
  }

  addNewCompany(trusteeOf = null, initValue = null) {
    const companyModel = new IndividualCompanyModel();
    const companyFg = this.formBuilderService.convertObjectToFormGroup(companyModel);
    this.formBuilderService.setValidationRules(new IndividualCompanyModel(), companyFg);
    if (initValue) {
      companyFg.patchValue(initValue);
    }
    companyFg.get('trusteeOf').patchValue(trusteeOf);
    if (this.isSMSF) {
      companyFg.get('companyACN').clearValidators();
      companyFg.get('companyACN').updateValueAndValidity();
    }

    this.companyList.push(companyFg);
  }

  addNewIndividual(
    relationshipType = null,
    relatedTo = null,
    applicantType = null,
    defaultValue = null
  ) {
    let indivFg: UntypedFormGroup;
    let addPersonModel: any;

    if (this.selectedType && this.selectedType.id === 'individual-with-non-applicant') {
      addPersonModel = new IndividualNonApplicantModel();
      indivFg = this.formBuilderService.convertObjectToFormGroup(addPersonModel);
      this.formBuilderService.setValidationRules(new IndividualNonApplicantModel(), indivFg);
    } else {
      addPersonModel = new IndividualPersonModel();
      indivFg = this.formBuilderService.convertObjectToFormGroup(addPersonModel);
      // add the relationship
      this.formBuilderService.setValidationRules(new IndividualPersonModel(), indivFg);
    }

    if (!indivFg.get('phones')) {
      indivFg.addControl('phones', new UntypedFormArray([]));
    }
    if (!indivFg.get('relationships')) {
      indivFg.addControl('relationships', new UntypedFormArray([]));
    }
    if (!indivFg.get('previousAddresses')) {
      indivFg.addControl('previousAddresses', new UntypedFormArray([]));
    }

    if (defaultValue) {
      const {
        relationships,
        previousAddresses
      } = defaultValue || {};

      defaultValue = _.omit(defaultValue, ['relationships', 'companyRelationships']);
      indivFg.patchValue(defaultValue);
      if (defaultValue.phones && defaultValue.phones.length >= 1) {
        _.forEach(defaultValue.phones, (phone) => {
          const PhoneFormGroup = this.formBuilderService.convertObjectToFormGroup(new IndividualPhoneModel());
          this.formBuilderService.setValidationRules(new IndividualPhoneModel(), PhoneFormGroup);
          const phonesFA = indivFg.get('phones') as UntypedFormArray;
          if (phonesFA) {
            phonesFA.push(PhoneFormGroup, {emitEvent: false});
            phonesFA.at(phonesFA.length - 1).patchValue(phone);
          }
        });
      }
      if (relationships && relationships.length >= 1) {
        const IndividualRelationshipFormGroup = this.formBuilderService.convertObjectToFormGroup(new IndividualRelationshipModel());
        this.formBuilderService.setValidationRules(new IndividualRelationshipModel(), IndividualRelationshipFormGroup);
        _.forEach(relationships, (relationship) => {
          const relationshipFA = indivFg.get('relationships') as UntypedFormArray;
          if (relationshipFA) {
            relationshipFA.push(IndividualRelationshipFormGroup, {emitEvent: false});
            relationshipFA.at(relationshipFA.length - 1).patchValue(relationship);
          }
        });
      }
      if (previousAddresses && previousAddresses.length >= 1) {
        const PreviousAddressFG = this.formBuilderService.convertObjectToFormGroup(new PreviousAddressModel());
        this.formBuilderService.setValidationRules(new PreviousAddressModel(), PreviousAddressFG);
        _.forEach(previousAddresses, (pa) => {
          const previousAddressesFA = indivFg.get('previousAddresses') as UntypedFormArray;
          if (previousAddressesFA) {
            previousAddressesFA.push(PreviousAddressFG, {emitEvent: false});
            previousAddressesFA.at(previousAddressesFA.length - 1).patchValue(pa);
          }
        });
      }
    }

    if (relationshipType && relatedTo) {
      const fa = new UntypedFormArray([]);
      if (!this.isSMSF) {
        fa.setValidators(Validators.compose([notSameCompanyRelationShipValidator()]));
      }

      const relationship = new IndividualRelationshipModel();
      relationship.from.value = this.personList.length - 1;
      relationship.type.value = relationshipType;
      relationship.to.value = relatedTo;
      const fg = this.formBuilderService.convertObjectToFormGroup(relationship);
      fg.get('type').setValidators([Validators.required]);
      fg.get('to').setValidators([Validators.required]);
      fa.push(fg);
      this.formBuilderService.setValidationRules(new IndividualRelationshipModel(), fg);
      indivFg.setControl('companyRelationships', fa);
    }

    if (applicantType) {
      indivFg.get('borrowerGuarantor').patchValue(applicantType);
    }

    this.personList.push(indivFg);
  }

  handleApplicationTypeChange(from = null, to = null) {
    if (from && from !== to) {
      this.formGroup.get('trustType').reset();
      this.formGroup.get('trustABN').reset();
      this.formGroup.get('trustABNResult').reset();
      this.formGroup.get('trustABNResult').reset();
      this.formGroup.get('companyACN').reset();
      this.formGroup.get('companyACNResult').reset();
      this.formGroup.get('borrowerType').reset();
      // this.formGroup.get('companyACNConfirmation').reset();
    } else if (!from && to && !this.applicationType) {
      this.applicationType = to;
    }

    switch (this.applicationType) {
      case 'new-trust':
        this.formGroup.get('borrowerType').addValidators(Validators.required);
        if (!this.trustList.at(this.opts.trustIndex)) {
          this.addNewTrust();
        }
        break;
      case 'new-company':
        this.formGroup.get('borrowerType').patchValue('company');
        this.clearFormArray(this.trustList);
        let initValue = null;
        if (this.companyList.value.length) {
          initValue = this.companyList.at(0).value;
        }
        this.clearFormArray(this.companyList);
        if (!this.companyList.at(this.getCompanyIndex())) {
          this.addNewCompany(null, initValue);
        }
        break;
      case 'new-personal':
      case 'new-personal-with-non-applicant':
        this.clearFormArray(this.trustList);
        this.clearFormArray(this.companyList);
        if (this.personList.length === 0) {
          this.addNewIndividual();
        }
        break;
      case 'continue':
      case 'old-personal':
        break;
    }
  }

  toggleCompanySearches(isEnabled) {
    if (isEnabled) {
      this.formGroup
        .get('companyACN')
        .setValidators([Validators.pattern(this.abnAcnValidator)]);
    } else {
      this.formGroup.get('companyACN').clearValidators();
    }
    this.formGroup.get('companyACN').updateValueAndValidity();
  }

  toggleTrustSearches(isEnabled) {
    if (isEnabled) {
      if (this.smsfEnabled) {
        this.formGroup.get('trustType').addValidators(Validators.required);
      }
      if (!this.formGroup.value.trustNoABN) {
        this.formGroup
          .get('trustABN')
          .setValidators([Validators.required, Validators.pattern(this.abnAcnValidator)]);
      }
    } else {
      this.formGroup.get('trustABN').clearValidators();
      this.formGroup.get('trustABN').setErrors(null);
      this.formGroup.get('trustType').clearValidators();
      this.formGroup.get('trustType').setErrors(null);
    }

    this.formGroup.get('trustType').updateValueAndValidity();
    this.formGroup.get('trustABN').updateValueAndValidity();
  }

  handleBorrowerTypeChange(borrowerType) {
    this.selectedType = this.getBorrowingType(borrowerType);
    switch (borrowerType) {
      case 'individual':
        this.formGroup.get('borrowerCount').patchValue(1);
        this.toggleCompanySearches(false);
        this.toggleTrustSearches(false);
        break;
      case 'individual-with-non-applicant':
        this.formGroup.get('borrowerCount').patchValue(2);
        this.toggleCompanySearches(false);
        this.toggleTrustSearches(false);
        break;
      case 'joint-individual':
        this.formGroup.get('borrowerCount').patchValue(2);
        this.toggleCompanySearches(false);
        this.toggleTrustSearches(false);
        break;
      case 'company':
        this.toggleCompanySearches(true);
        this.toggleTrustSearches(false);
        break;
      case 'trust-with-company':
        const companyIndex = this.getCompanyIndex();
        if (!this.companyList.at(companyIndex)) {
          this.addNewCompany(this.opts.trustIndex);
        } else if (this.companyList.at(companyIndex)) {
          this.companyList.at(companyIndex).get('trusteeOf').patchValue(this.opts.trustIndex);
        }
        if (this.isSMSFPanel) {
          this.formGroup.get('companyACN').clearValidators();
        } else {
          this.formGroup
            .get('companyACN')
            .setValidators([Validators.pattern(this.abnAcnValidator)]);
        }
        this.formGroup.get('companyACN').updateValueAndValidity({emitEvent: false});
        this.toggleCompanySearches(true);
        this.toggleTrustSearches(true);
        break;
      case 'trust-with-individual':
        const linkedCompany = _.findIndex(
          this.companyList.value,
          (v) => v.trusteeOf === this.opts.trustIndex
        );
        if (linkedCompany !== -1) {
          this.companyList.removeAt(linkedCompany);
          // remove directors related to that company
        }
        this.formGroup.get('companyACN').clearValidators();
        this.formGroup.get('companyACN').updateValueAndValidity({emitEvent: false});
        this.toggleTrustSearches(true);
        this.toggleCompanySearches(false);
        break;
    }

    if (borrowerType && this.isSMSF) {
      switch (borrowerType) {
        case 'trust-with-individual':
        case 'trust-with-company':
          const securityTrust = _.find(
            this.companyList.value,
            (company) => company.trusteeOf === 1
          );
          if (!securityTrust) {
            this.addNewCompany(1);
          }
          break;
        default:
          break;
      }
    }
  }

  getCompanyIndex() {
    let index = 0;
    const borrowerType = this.formGroup.value.borrowerType;
    if (borrowerType === 'trust-with-company') {
      index = _.findIndex(
        this.companyList.value,
        (c) =>
          c.trusteeOf === null || c.trusteeOf === undefined || c.trusteeOf === this.opts.trustIndex
      );
      if (index === -1) {
        // if not existing -- add a new company
        index = this.companyList.length;
      }
    }
    return index;
  }

  getBorrowingTypeMapping(borrowerType = null) {
    const companyTrusteeOf = _.findIndex(this.companyList.value, [
      'trusteeOf',
      this.opts.trustIndex,
    ]);
    const borrowerTypeMapping = {
      company: {
        applicantType: 'guarantor',
        type: 'director',
        to: `company-${this.getCompanyIndex()}`,
      },
      'trust-with-company': {
        applicantType: 'guarantor',
        type: 'director',
        to: `company-${companyTrusteeOf}`,
      },
      'trust-with-individual': {
        applicantType: 'guarantor',
        type: 'trustee',
        to: `trust-${this.opts.trustIndex}`,
      }
    };
    return borrowerTypeMapping[borrowerType || this.formGroup.value.borrowerType] || {};
  }

  handleBorrowerCountChange(borrowerCount) {
    // do nothing if `isSMSFPanel` is true.
    if (this.isSMSFPanel) {
      return;
    }

    const {type} = this.getBorrowingTypeMapping();
    const borrowingType = this.getBorrowingType();
    const borrowerType = borrowingType?.id || this.formGroup.value.borrowerType;
    this.borrowingTypeChange.emit({
      borrowingType,
      borrowerCount,
    });

    // let curLength = this.personList.length;
    this.dataHandlerService.sendBorrowerTypeData({borrowingType: type});
    this.dataHandlerService.sendBorrowerCountData(borrowerCount);
    this.dataHandlerService.sendMainBorrowerType(borrowerType);

    if (this.isSMSF) {
      this.handleSecurityTrust(null);
    }

    // emit to a dataHandlerSubject
    this.dataHandlerService.sendPersonListData(this.personList);
  }

  selectCompanyAbnRecord(abnResult: any) {
    if (this.formGroup.value.trustNoABN && this.applicationType === 'new-trust') {
      this.selectAbnRecord(abnResult, 'trust', this.opts.trustIndex);
    }
    this.selectAbnRecord(abnResult, 'company', this.getCompanyIndex());
  }

  selectAbnRecord(abnResult: any, destination: any, destinationIdx = 0, formGroup?: UntypedFormGroup, controlName?) {
    if (abnResult) {
      const item = this.extractABNDetails(abnResult);
      switch (destination) {
        case 'trust':
          if (destinationIdx > -1) {
            while (!this.trustList.at(destinationIdx)) {
              this.addNewTrust();
            }
            const trustNoABN = this.formGroup.value.trustNoABN;
            this.trustList.at(destinationIdx).patchValue({
              trustABN: trustNoABN ? null : item.abn,
              trustACN: item.acn,
              trustName: trustNoABN ? null : item.name,
            });
          }
          break;
        case 'company':
          if (destinationIdx > -1) {
            while (!this.companyList.at(destinationIdx)) {
              this.addNewCompany();
            }
            this.companyList.at(destinationIdx).patchValue({
              companyABN: item.abn,
              companyACN: item.acn,
              companyName: item.name,
              tradingName: item.tradingName,
              acnRegistered: item.acnRegistered,
              trusteeOf: this.applicationType !== 'new-company' ? this.opts.trustIndex : null,
            });

            this.formGroup.patchValue({
              acnRegistered: item.acnRegistered,
            });
          }
          break;
      }
    } else {
      formGroup.get(controlName).patchValue('');
    }
  }
  public getBorrowerCount (borrowerType = this.formGroup.value.borrowerType) {
    return borrowerType === 'individual-with-non-applicant' ? 2 : this.formGroup.value.borrowerCount;
  }
  public getBorrowingType (borrowerType = this.formGroup.value.borrowerType) {
    return this.borrowingTypes.find((type) => borrowerType === type.id);
  }
  public clearFormArray(formArray: UntypedFormArray) {
    while (formArray && formArray.length !== 0) {
      formArray.removeAt(0);
    }
  }
}

